import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from 'api';
import { getActingProfileUserId } from 'utils/selectedAccount';

/**
 * Canonical list of profile-section keys the client profile tracks. Kept here
 * so both the ClientProfile sidebar and the QuickActions "all forms complete"
 * indicator agree on what "done" means for a candidate.
 */
export const PROFILE_SECTION_KEYS = [
    'personal_info',
    'additional_details',
    'spouse',
    'family_member',
    'language',
    'education',
    'work_experience',
    'relative_abroad',
    'business',
    'job_offer',
] as const;

export function areAllCompletedFormFieldsTrue(
    forms: Record<string, unknown> | null | undefined,
    requiredKeys: readonly string[] = PROFILE_SECTION_KEYS
): boolean {
    if (!forms || requiredKeys.length === 0) {
        return false;
    }
    return requiredKeys.every((key) => forms[key] === true);
}

export function isCompletedAssessmentUnset(value: unknown): boolean {
    if (value == null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    return false;
}

/**
 * Returns the candidate's completed-forms map (`{ personal_info: true, ... }`)
 * keyed by the snake_case labels from `utils/formLabelsToKey`.
 *
 * Used in ClientProfile to show completion chips in the sidebar (view + edit
 * modes) and by the edit pane to refresh state after a save. Centralises the
 * previously copy-pasted fetch/dedupe logic from salesOnboarding/FormPage.
 *
 * Also exposes `allCompleted`, true when every key in `requiredKeys`
 * (default: the canonical profile section list) is marked complete.
 */
export const useCompletedForms = (
    candidateID: number | undefined | null,
    requiredKeys: readonly string[] = PROFILE_SECTION_KEYS
) => {
    const [completedForms, setCompletedForms] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const inFlight = useRef(false);

    const refresh = useCallback(async () => {
        const profileId = getActingProfileUserId() ?? candidateID;
        if (!Number.isFinite(profileId) || !profileId || profileId <= 0) {
            setCompletedForms({});
            return;
        }
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        try {
            const resp = await api.completedForms.getCompletedForms(profileId);
            if (resp?.data) {
                setCompletedForms(resp.data);
            }
        } catch (e) {
            console.error('Failed to load completed forms', e);
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, [candidateID]); // acting profile is read at refresh time from selected-account storage

    useEffect(() => {
        refresh();
    }, [refresh]);

    const allCompleted = useMemo(
        () => areAllCompletedFormFieldsTrue(completedForms, requiredKeys),
        [completedForms, requiredKeys]
    );

    return { completedForms, refresh, loading, allCompleted };
};
